import { index, onchainTable } from "ponder";

export const nameSales = onchainTable(
  "name_sales",
  (t) => ({
    /**
     * Unique and deterministic identifier of the onchain event associated with the sale.
     *
     * Composite key format: "{chainId}-{blockNumber}-{logIndex}" (e.g., "1-1234567-5")
     */
    id: t.text().primaryKey(),

    /**
     * The chain where the sale occurred.
     */
    chainId: t.int8({ mode: "number" }).notNull(),

    /**
     * The block number on chainId where the sale occurred.
     */
    blockNumber: t.bigint().notNull(),

    /**
     * The log index position of the sale event within blockNumber.
     */
    logIndex: t.integer().notNull(),

    /**
     * The EVM transaction hash on chainId associated with the sale.
     */
    transactionHash: t.hex().notNull(),

    /**
     * The Seaport order hash.
     */
    orderHash: t.hex().notNull(),

    /**
     * The address of the contract on chainId that manages tokenId.
     */
    contractAddress: t.hex().notNull(),

    /**
     * The tokenId managed by contractAddress that was sold.
     *
     * In a general context (outside of TokenScope) ERC1155 NFTs may have
     * multiple copies, however TokenScope guarantees that all indexed NFTs
     * never have an amount / balance > 1.
     */
    tokenId: t.bigint().notNull(),

    /**
     * The CAIP-19 Asset Namespace of the token that was sold. Either `erc721` or `erc1155`.
     *
     * @see https://chainagnostic.org/CAIPs/caip-19
     */
    assetNamespace: t.text().notNull(),

    /**
     * The CAIP-19 Asset ID of token that was sold. This is a globally unique reference to the
     * specific asset in question.
     *
     * @see https://chainagnostic.org/CAIPs/caip-19
     */
    assetId: t.text().notNull(),

    /**
     * The namehash (Node) of the ENS domain that was sold.
     */
    domainId: t.hex().notNull(),

    /**
     * The account that bought the token controlling ownership of domainId from
     * the seller for the amount of currency associated with the sale.
     */
    buyer: t.hex().notNull(),

    /**
     * The account that sold the token controlling ownership of domainId to
     * buyer for the amount of currency associated with the sale.
     */
    seller: t.hex().notNull(),

    /**
     * Currency of the payment (ETH, USDC or DAI) from buyer to seller in exchange for tokenId.
     */
    currency: t.text().notNull(),

    /**
     * The amount of currency paid from buyer to seller in exchange for tokenId.
     *
     * Denominated in the smallest unit of currency.
     *
     * Amount interpretation depends on currency:
     * - ETH/WETH: Amount in wei (1 ETH = 10^18 wei)
     * - USDC: Amount in micro-units (1 USDC = 10^6 units)
     * - DAI: Amount in wei-equivalent (1 DAI = 10^18 units)
     */
    amount: t.bigint().notNull(),

    /**
     * Unix timestamp of the block timestamp when the sale occurred.
     */
    timestamp: t.bigint().notNull(),
  }),
  (t) => ({
    idx_domainId: index().on(t.domainId),
    idx_assetId: index().on(t.assetId),
    idx_buyer: index().on(t.buyer),
    idx_seller: index().on(t.seller),
    idx_timestamp: index().on(t.timestamp),
  }),
);

export const nameTokens = onchainTable(
  "name_tokens",
  (t) => ({
    /**
     * The CAIP-19 Asset ID of the token.
     *
     * This is a globally unique reference to the token.
     *
     * @see https://chainagnostic.org/CAIPs/caip-19
     */
    id: t.text().primaryKey(),

    /**
     * The namehash (Node) of the ENS name associated with the token.
     *
     * Note: An ENS name may have more than one distinct token across time. It is
     * also possible for multiple distinct tokens for an ENS name to have
     * a mintStatus of `minted` at the same time. For example:
     * - When a direct subname of .eth is wrapped by the NameWrapper. This state
     *   has one minted token for the name managed by the BaseRegistrar (this
     *   token will be owned by the NameWrapper) and another minted token for
     *   the name managed by the NameWrapper (owned by the effective owner of
     *   the name).
     * - When a direct subname of .eth is wrapped by the NameWrapper and then
     *   unwrapped. This state has one minted token (managed by the BaseRegistrar)
     *   and another burned token (managed by the NameWrapper).
     */
    domainId: t.hex().notNull(),

    /**
     * The chain that manages the token.
     */
    chainId: t.int8({ mode: "number" }).notNull(),

    /**
     * The address of the contract on chainId that manages the token.
     */
    contractAddress: t.hex().notNull(),

    /**
     * The tokenId of the token managed by contractAddress.
     *
     * In a general context (outside of TokenScope) ERC1155 NFTs may have
     * multiple copies, however TokenScope guarantees that all indexed NFTs
     * never have an amount / balance > 1.
     */
    tokenId: t.bigint().notNull(),

    /**
     * The CAIP-19 Asset Namespace of the token. Either `erc721` or `erc1155`.
     *
     * @see https://chainagnostic.org/CAIPs/caip-19
     */
    assetNamespace: t.text().notNull(),

    /**
     * The account that owns the token.
     *
     * Value is zeroAddress if and only if mintStatus is `burned`.
     *
     * Note: The owner of the token for a given domainId may differ from the
     * owner of the associated node in the registry. For example:
     * - Consider the case where address X owns the ENS name `foo.eth` in
     *   both the BaseRegistrar and the Registry. If X sends a request directly
     *   to the Registry to transfer ownership to Y, ownership of `foo.eth` will
     *   be transferred to Y in the Registry but not in the BaseRegistrar.
     * - ... for the case above, the BaseRegistrar implements a `reclaim`
     *   allowing the owner of the name in the BaseRegistrar to reclaim ownership
     *   of the name in the Registry.
     *
     * Note: When a name is wrapped by the NameWrapper, the owner of the token
     * in the BaseRegistrar is the NameWrapper, while a new token for the name is
     * minted by the NameWrapper and owned by the effective owner of the name.
     */
    owner: t.hex().notNull(),

    /**
     * The mint status of the token. Either `minted` or `burned`.
     *
     * After we index a NFT we never delete it from our index. Instead, when an
     * indexed NFT is burned onchain we retain its record and update its mint
     * status as `burned`. If a NFT is minted again after it is burned its mint
     * status is updated to `minted`.
     */
    mintStatus: t.text().notNull(),
  }),
  (t) => ({
    idx_domainId: index().on(t.domainId),
    idx_owner: index().on(t.owner),
  }),
);
